import { HStack, Icon } from '@musinsa/mwms-ds'
import GridTable from '@/components/table/GridTable'
import {
  AggregatedInboundRow,
  CATEGORY_GLOBAL_SETTLEMENT_FLAG,
  INBOUND_SETTLEMENT_UNIT_TYPE_FLAG,
  ServiceTypeKey,
  SETTLEMENT_DETAIL_SKU_VIEW_FLAG,
  SettlementStatisticDetailItem,
  SettlementStatisticsDetailSummaryRes,
} from '@/services/settlement/settlementManagement/types'
import Text from '@/components/content/Text'
import { useCallback, useMemo, useRef } from 'react'
import { useGetCountries } from '@/services/commonCode/useCommonCodeQueries'
import { AgGridReact } from 'ag-grid-react'
import { GridTableProps } from '@/components/table/grid_types'
import { colorSystem, fontSystem } from '@/styles/theme'
import { Divider } from '@/components/content/Divider'
import { getColumnDefs } from './columnDefinitions'
import { GridTooltip } from '@/pages/settlement/SettlementManagement/components/GridTooltip'
import { SETTLEMENT_DETAIL_EXPORT_AUDIT, SETTLEMENT_DETAIL_GRID_KEY_BY_TAB } from '@/pages/settlement/SettlementManagement/consts/consts'
import { GRID_SIZE } from '@/utils/grid/grid'
import { defaultColDef } from '@/components/table/grid_table_options'
import { useFeatureFlag, useFeatureFlagBoolean } from '@/services/feature/useFeatureQueries'
import styled from 'styled-components'

// 합계 행의 첫 번째 컬럼 필드 반환
const getFirstColumnField = (currentTab: ServiceTypeKey): string => {
  const firstColumnFieldMap: Record<ServiceTypeKey, string> = {
    ALL: 'settlementDate',
    INVENTORY: 'settlementDate',
    INBOUND: 'settlementDate',
    OUTBOUND_TO_CUSTOMER: 'settlementDate',
    OUTBOUND_TO_STORE: 'settlementDate',
    RETURN_INBOUND: 'settlementDate',
    MEASUREMENT: 'settlementDate',
    ETC_ADJUSTMENT: 'createdDate',
  }
  return firstColumnFieldMap[currentTab]
}

export type SettlementManagementDetailTableProps<Tab extends ServiceTypeKey> = Pick<
  GridTableProps<string>,
  'isLoading' | 'onExportData' | 'onChangePage'
> & {
  currentTab: Tab
  // LP8238: INBOUND 탭은 집계 표시 row(AggregatedInboundRow)를 전달한다 (§3.2)
  rows: Array<SettlementStatisticDetailItem<Tab> | AggregatedInboundRow | SettlementStatisticsDetailSummaryRes>
  summaryRowData?: SettlementStatisticsDetailSummaryRes
  pageInfo?: PageInfoType
  /** 신규 정산 기준 적용 여부 — 컬럼명 분기 (출고일자/입고일자 vs 구매확정일자/클레임완료일자) */
  cutoverApplied?: boolean
}

export function SettlementManagementDetailTable<Tab extends ServiceTypeKey>({
  currentTab,
  summaryRowData,
  rows,
  isLoading,
  pageInfo,
  onChangePage,
  onExportData,
  cutoverApplied = false,
}: SettlementManagementDetailTableProps<Tab>) {
  const $grid = useRef<AgGridReact>(null)

  // LP8519: 박스단위 입고 정산 feature flag (OFF 면 레거시 INBOUND 컬럼). react-query 캐시로 집계 hook 과 동일 키 공유
  const { data: isInboundSettlementUnitTypeEnabled } = useFeatureFlag(INBOUND_SETTLEMENT_UNIT_TYPE_FLAG)

  // LP9182: 국가 차원 노출 flag (OFF 면 국가 컬럼 미노출)
  const { data: isCategoryGlobalSettlementEnabled } = useFeatureFlag(CATEGORY_GLOBAL_SETTLEMENT_FLAG)

  // LOGISPRD-14189: sku_id 노출 flag (OFF·조회 실패면 현행 컬럼)
  const { data: isSkuViewEnabled } = useFeatureFlagBoolean(SETTLEMENT_DETAIL_SKU_VIEW_FLAG.key, SETTLEMENT_DETAIL_SKU_VIEW_FLAG.objectKey)

  // LP9182 — 전체(ALL) 탭 국가집합(countries 코드 CSV) → code→name 매핑. useGetCountries 순서로 고정 정렬(§4.3). per-cost 탭은 row.country.name 직접 사용(resolver 불요)
  // flag OFF 면 조회 자체 차단(no-op·실패 알럿 방지)
  const { data: countries } = useGetCountries(!!isCategoryGlobalSettlementEnabled)
  const codeToName = useMemo(() => new Map((countries ?? []).map((c) => [c.code, c.name])), [countries])
  const orderIndex = useMemo(() => new Map((countries ?? []).map((c, i) => [c.code, i])), [countries])
  const renderCountries = useCallback(
    (csv?: string | null) => {
      if (!csv) return '-'
      const names = csv
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .sort((a, b) => (orderIndex.get(a) ?? 99) - (orderIndex.get(b) ?? 99))
        .map((code) => codeToName.get(code) ?? code)
      return names.length ? names.join(', ') : '-'
    },
    [codeToName, orderIndex],
  )

  const columnDefs = useMemo(
    () =>
      getColumnDefs(
        currentTab,
        cutoverApplied,
        isInboundSettlementUnitTypeEnabled,
        renderCountries,
        isCategoryGlobalSettlementEnabled,
        isSkuViewEnabled,
      ),
    [currentTab, cutoverApplied, isInboundSettlementUnitTypeEnabled, renderCountries, isCategoryGlobalSettlementEnabled, isSkuViewEnabled],
  )

  const onGridReady = () => {
    $grid.current?.api?.setHeaderHeight(40)
  }

  return (
    <TableWrap full gap={16} currentTab={currentTab}>
      <GridTable
        ref={$grid}
        gridKey={SETTLEMENT_DETAIL_GRID_KEY_BY_TAB[currentTab]}
        onGridReady={onGridReady}
        isLoading={isLoading}
        radius="0 0 8px 8px"
        title={
          <HStack gap={12} align="center" style={{ flex: '0 0 auto', color: colorSystem.text.common.tertiary }}>
            <Text kind="Body_12_bold" color="inherit">
              {rows.length}
            </Text>
            <Divider variant="vertical" style={{ height: '8px' }} />
            <Text kind="Body_12_bold" color="inherit">
              {pageInfo?.totalCount ?? rows.length}
            </Text>
          </HStack>
        }
        summaryRowData={
          summaryRowData
            ? {
                ...summaryRowData,
                quantity: summaryRowData?.totalQuantity,
                [getFirstColumnField(currentTab)]: '합계',
              }
            : undefined
        }
        defaultColDef={{ ...defaultColDef, suppressMenu: true, tooltipComponent: GridTooltip, autoHeight: true }}
        columnDefs={columnDefs}
        rowData={rows}
        pagination={Boolean(currentTab !== 'ALL')}
        countPerPage={GRID_SIZE.SETTLEMENT}
        totalCount={pageInfo?.totalCount ?? 0}
        currentPage={pageInfo?.page}
        onChangePage={onChangePage}
        onExportData={onExportData}
        exportType="private"
        exportWithPassword={false}
        exportArea="DETAIL"
        exportAudit={SETTLEMENT_DETAIL_EXPORT_AUDIT}
        exportButtonLabel={
          <Text kind="Body_14_medium">
            <Icon name="download" size={16} style={{ marginRight: '4px' }} /> 엑셀 다운로드
          </Text>
        }
        tooltipShowDelay={0}
        enableBrowserTooltips={false}
      />
    </TableWrap>
  )
}

const TableWrap = styled(HStack)<{ currentTab: ServiceTypeKey }>`
  .ag-cell-wrapper {
    height: 52px;
  }

  .ag-floating-top {
    min-height: 52px !important;

    * {
      ${fontSystem.Body_12_bold};
    }
  }
`
